(function () {
  const enableAutoplay =
    typeof window.enableAutoplay !== "undefined" ? window.enableAutoplay : false;

  class ProductGallery {
    constructor(container) {
      this.container = container;
      this.mainImages = Array.from(container.querySelectorAll(".shop-main-image"));
      this.thumbnails = Array.from(container.querySelectorAll(".shop-thumbnail"));
      this.dots = Array.from(container.querySelectorAll(".shop-dot"));

      this.prevArrows = Array.from(
        container.querySelectorAll(".shop-desktop-prev-arrow, .shop-mobile-prev-arrow")
      );

      this.nextArrows = Array.from(
        container.querySelectorAll(".shop-desktop-next-arrow, .shop-mobile-next-arrow")
      );

      this.indicatorsContainer = container.querySelector(
        ".shop-indicators, #thumbnail-indicators"
      );

      this.currentIndex = 0;
      this.maxIndex = this.mainImages.length - 1;

      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchEndX = 0;
      this.touchEndY = 0;
      this.isTouchingMainImage = false;
      this.swipeThreshold = 34;
      this.lastChangeTime = 0;

      this.init();
    }

    init() {
      this.preloadAllImages();
      this.createIndicators();
      this.attachEvents();
      this.attachSwipeEvents();
      this.changeImage(0, false);
    }

    attachEvents() {
      this.thumbnails.forEach((thumb, fallbackIndex) => {
        thumb.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();

          const index = this.getIndexFromThumbnail(thumb, fallbackIndex);
          this.changeImage(index, true);
        });
      });

      this.dots.forEach((dot, index) => {
        dot.addEventListener("click", (event) => {
          event.preventDefault();
          this.changeImage(index, true);
        });

        dot.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            this.changeImage(index, true);
          }
        });
      });

      this.prevArrows.forEach((arrow) => {
        arrow.addEventListener(
          "touchstart",
          (event) => {
            event.stopPropagation();
            this.resetSwipeState();
          },
          { passive: true }
        );

        arrow.addEventListener(
          "touchmove",
          (event) => {
            event.stopPropagation();
          },
          { passive: true }
        );

        arrow.addEventListener(
          "touchend",
          (event) => {
            event.stopPropagation();
            this.resetSwipeState();
          },
          { passive: true }
        );

        arrow.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigate(-1);
        });
      });

      this.nextArrows.forEach((arrow) => {
        arrow.addEventListener(
          "touchstart",
          (event) => {
            event.stopPropagation();
            this.resetSwipeState();
          },
          { passive: true }
        );

        arrow.addEventListener(
          "touchmove",
          (event) => {
            event.stopPropagation();
          },
          { passive: true }
        );

        arrow.addEventListener(
          "touchend",
          (event) => {
            event.stopPropagation();
            this.resetSwipeState();
          },
          { passive: true }
        );

        arrow.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.navigate(1);
        });
      });

      if (this.indicatorsContainer) {
        this.indicatorsContainer.addEventListener("click", (event) => {
          if (!event.target.classList.contains("shop-indicator-dot")) return;

          const index = parseInt(event.target.getAttribute("data-index"), 10);
          if (!Number.isNaN(index)) {
            this.changeImage(index, true);
          }
        });
      }
    }

    attachSwipeEvents() {
      const imageContainer = this.container.querySelector(".shop-main-image-wrapper");
      if (!imageContainer) return;

      imageContainer.addEventListener(
        "touchstart",
        (event) => {
          if (event.target.closest("button")) {
            this.resetSwipeState();
            return;
          }

          if (!event.changedTouches || !event.changedTouches.length) return;

          this.touchStartX = event.changedTouches[0].clientX;
          this.touchStartY = event.changedTouches[0].clientY;
          this.touchEndX = this.touchStartX;
          this.touchEndY = this.touchStartY;
          this.isTouchingMainImage = true;
        },
        { passive: true }
      );

      imageContainer.addEventListener(
        "touchmove",
        (event) => {
          if (!this.isTouchingMainImage) return;
          if (!event.changedTouches || !event.changedTouches.length) return;

          this.touchEndX = event.changedTouches[0].clientX;
          this.touchEndY = event.changedTouches[0].clientY;

          const deltaX = this.touchEndX - this.touchStartX;
          const deltaY = this.touchEndY - this.touchStartY;

          if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
            event.preventDefault();
          }
        },
        { passive: false }
      );

      imageContainer.addEventListener(
        "touchend",
        (event) => {
          if (!this.isTouchingMainImage) return;

          if (event.changedTouches && event.changedTouches.length) {
            this.touchEndX = event.changedTouches[0].clientX;
            this.touchEndY = event.changedTouches[0].clientY;
          }

          this.handleSwipeGesture();
        },
        { passive: true }
      );

      imageContainer.addEventListener(
        "touchcancel",
        () => {
          this.resetSwipeState();
        },
        { passive: true }
      );
    }

    getIndexFromThumbnail(thumb, fallbackIndex = 0) {
      const mediaId = thumb.getAttribute("data-media-id");

      if (mediaId) {
        const mediaIndex = this.mainImages.findIndex((media) => {
          return media.getAttribute("data-media-id") === mediaId;
        });

        if (mediaIndex !== -1) return mediaIndex;
      }

      const dataIndex = parseInt(thumb.getAttribute("data-index"), 10);
      return Number.isNaN(dataIndex) ? fallbackIndex : dataIndex;
    }

    getVisibleIndexes() {
      const indexes = this.thumbnails
        .filter((thumb) => {
          if (thumb.classList.contains("visually-hidden")) return false;
          const style = window.getComputedStyle(thumb);
          return style.display !== "none" && style.visibility !== "hidden";
        })
        .map((thumb, fallbackIndex) => this.getIndexFromThumbnail(thumb, fallbackIndex))
        .filter((index) => !Number.isNaN(index) && index >= 0 && index <= this.maxIndex);

      return indexes.length ? indexes : this.mainImages.map((_, index) => index);
    }

    getCurrentIndex() {
      if (
        Number.isInteger(this.currentIndex) &&
        this.currentIndex >= 0 &&
        this.currentIndex <= this.maxIndex
      ) {
        return this.currentIndex;
      }

      const activeMediaIndex = this.mainImages.findIndex((media) => {
        return media.classList.contains("active") || media.style.display === "block";
      });

      return activeMediaIndex !== -1 ? activeMediaIndex : 0;
    }

    navigate(direction) {
      const now = Date.now();

      if (now - this.lastChangeTime < 120) return;

      const visibleIndexes = this.getVisibleIndexes();
      const current = this.getCurrentIndex();

      let currentPosition = visibleIndexes.indexOf(current);

      if (currentPosition === -1) {
        currentPosition = 0;
      }

      let nextPosition = currentPosition + direction;

      if (nextPosition >= visibleIndexes.length) nextPosition = 0;
      if (nextPosition < 0) nextPosition = visibleIndexes.length - 1;

      this.changeImage(visibleIndexes[nextPosition], true);
    }

    handleSwipeGesture() {
      const deltaX = this.touchEndX - this.touchStartX;
      const deltaY = this.touchEndY - this.touchStartY;

      this.resetSwipeState();

      if (Math.abs(deltaX) <= this.swipeThreshold) return;
      if (Math.abs(deltaX) <= Math.abs(deltaY)) return;

      if (deltaX < 0) {
        this.navigate(1);
      } else {
        this.navigate(-1);
      }
    }

    resetSwipeState() {
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchEndX = 0;
      this.touchEndY = 0;
      this.isTouchingMainImage = false;
    }

    changeImage(index, shouldScrollThumbnail = true) {
      index = parseInt(index, 10);

      if (Number.isNaN(index) || index < 0 || index > this.maxIndex) return;

      const activeMedia = this.mainImages[index];
      if (!activeMedia) return;

      this.lastChangeTime = Date.now();
      this.currentIndex = index;

      this.ensureMediaLoaded(activeMedia);

      this.mainImages.forEach((media, mediaIndex) => {
        const isActive = mediaIndex === index;

        if (isActive) {
          media.classList.add("active");
          media.style.display = "block";
          media.style.opacity = "1";
          media.style.visibility = "visible";
          media.style.pointerEvents = "auto";
          media.style.zIndex = "2";
        } else {
          media.classList.remove("active");
          media.style.display = "none";
          media.style.opacity = "0";
          media.style.visibility = "hidden";
          media.style.pointerEvents = "none";
          media.style.zIndex = "1";
        }
      });

      this.thumbnails.forEach((thumb, fallbackIndex) => {
        const thumbIndex = this.getIndexFromThumbnail(thumb, fallbackIndex);
        thumb.classList.toggle("active", thumbIndex === index);
      });

      this.dots.forEach((dot, dotIndex) => {
        dot.classList.toggle("active", dotIndex === index);
      });

      if (this.indicatorsContainer) {
        Array.from(this.indicatorsContainer.children).forEach((dot, dotIndex) => {
          dot.classList.toggle("active", dotIndex === index);
        });
      }

      if (shouldScrollThumbnail) {
        this.scrollToThumbnail(index);
      }

      this.handleVideoAutoplay(index);

      window.setTimeout(() => {
        if (this.currentIndex === index) {
          const currentMedia = this.mainImages[index];

          if (currentMedia) {
            this.ensureMediaLoaded(currentMedia);
            currentMedia.classList.add("active");
            currentMedia.style.display = "block";
            currentMedia.style.opacity = "1";
            currentMedia.style.visibility = "visible";
            currentMedia.style.pointerEvents = "auto";
            currentMedia.style.zIndex = "2";
          }
        }
      }, 80);
    }

    preloadAllImages() {
      this.mainImages.forEach((media) => {
        this.ensureMediaLoaded(media);
      });
    }

    ensureMediaLoaded(media) {
      if (!media) return;

      if (media.tagName === "IMG") {
        this.loadImage(media);
        return;
      }

      const nestedImg = media.querySelector("img");
      if (nestedImg) {
        this.loadImage(nestedImg);
      }
    }

    loadImage(img) {
      if (!img) return;

      if (img.dataset.src) {
        img.setAttribute("src", img.dataset.src);
      }

      if (img.dataset.srcset) {
        img.setAttribute("srcset", img.dataset.srcset);
      }

      img.loading = "eager";
      img.style.opacity = "1";
      img.style.visibility = "visible";
    }

    scrollToThumbnail(index) {
      const thumbnail = this.thumbnails.find((thumb, fallbackIndex) => {
        return this.getIndexFromThumbnail(thumb, fallbackIndex) === index;
      });

      if (!thumbnail) return;

      const thumbnailTrack = thumbnail.closest(".shop-thumbnails");
      if (!thumbnailTrack) return;

      const containerWidth = thumbnailTrack.offsetWidth;
      const thumbnailWidth = thumbnail.offsetWidth;
      const thumbnailLeft = thumbnail.offsetLeft;
      const scrollPosition = thumbnailLeft - containerWidth / 2 + thumbnailWidth / 2;

      thumbnailTrack.scrollTo({
        left: scrollPosition,
        behavior: "smooth"
      });
    }

    createIndicators() {
      if (!this.indicatorsContainer) return;

      this.indicatorsContainer.innerHTML = "";

      this.mainImages.forEach((_, index) => {
        const dot = document.createElement("div");
        dot.className = "shop-indicator-dot" + (index === 0 ? " active" : "");
        dot.setAttribute("data-index", index);
        this.indicatorsContainer.appendChild(dot);
      });
    }

    handleVideoAutoplay(index) {
      this.mainImages.forEach((media) => {
        const video = media.querySelector("video");

        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      });

      const activeMedia = this.mainImages[index];
      if (!activeMedia) return;

      const videoToPlay = activeMedia.querySelector("video");

      if (videoToPlay && enableAutoplay) {
        videoToPlay.play().catch(() => {});
      }
    }

    updateWithVariant(variant) {
      let matchIndex = -1;

      if (variant && (variant.mediaId || variant.variantId || variant.imageUrl)) {
        this.mainImages.forEach((media, index) => {
          const mediaImg = media.tagName === "IMG" ? media : media.querySelector("img");
          const mediaSrc = mediaImg ? mediaImg.getAttribute("src") || "" : "";
          const mediaDataSrc = mediaImg ? mediaImg.dataset.src || "" : "";

          if (
            (variant.mediaId && media.dataset.mediaId == variant.mediaId) ||
            (variant.variantId && media.dataset.variantId == variant.variantId) ||
            (variant.imageUrl &&
              (mediaSrc.includes(variant.imageUrl) ||
                mediaDataSrc.includes(variant.imageUrl)))
          ) {
            matchIndex = index;
          }
        });
      }

      if (matchIndex >= 0) {
        this.changeImage(matchIndex, true);
      } else {
        this.changeImage(0, true);
      }
    }
  }

  const galleryRegistry = [];

  function initAll() {
    document.querySelectorAll(".product-gallery").forEach((container) => {
      if (!container.__galleryInstance) {
        const gallery = new ProductGallery(container);
        container.__galleryInstance = gallery;
        galleryRegistry.push(gallery);
      }
    });
  }

  document.addEventListener("variantImageSelected", function (event) {
    if (
      event.detail &&
      (event.detail.variantId || event.detail.mediaId || event.detail.imageUrl)
    ) {
      galleryRegistry.forEach((gallery) => gallery.updateWithVariant(event.detail));
    }
  });

  document.addEventListener("variant:imageChanged", function (event) {
    if (
      event.detail &&
      (event.detail.variantId || event.detail.mediaId || event.detail.imageUrl)
    ) {
      galleryRegistry.forEach((gallery) => gallery.updateWithVariant(event.detail));
    }
  });

  window.ProductGallery = {
    initAll,
    getAll: () => galleryRegistry
  };

  document.addEventListener("DOMContentLoaded", initAll);
})();